import { Component } from "react";
import { connect } from 'react-redux';
import axios from "axios";
import './FAQ.scss';
import FAQFolderList from './FAQFolderList';
import { getSellerHeaders, getSellerUrl } from "shared/http";
import { jiraConfluenceFolderList, jiraConfluencePageList, jiraConfluenceSearchPageView } from "shared/settings/apiconfig";
import { NoData } from "features/components/redirect/NoData";

const FAQ_BASE_PATH = '/help-center';

class FAQ extends Component {
    constructor(props) {
        super(props);
        this.state = {
            pageLoading: false,
            searchLoading: false,
            folders: [],
            pages: [],
            selectedFolderTitle: '',
            pageError: '',
            searchValue: '',
            errorpageredirect: false,
            errorType: ''
        };
        this.buyrId = this.props.user.id;
    }

    componentDidMount() {
        const folderId = this.getFolderId();

        if (folderId) {
            if (!this.loadFolderState(folderId)) {
                this.loadPages();
            }
            return;
        }

        this.loadFolders();
    }

    componentDidUpdate(prevProps) {
        const prevFolderId = this.getFolderId(prevProps);
        const currentFolderId = this.getFolderId();

        if (prevFolderId !== currentFolderId) {
            if (currentFolderId) {
                if (!this.loadFolderState(currentFolderId)) {
                    this.loadPages();
                }
            } else {
                this.loadFolders();
            }
            return;
        }

        if (currentFolderId && prevProps.location.state !== this.props.location.state) {
            if (!this.loadFolderState(currentFolderId)) {
                this.loadPages();
            }
        }
    }

    getFolderId = (props = this.props) => {
        const folderId = props.match?.params?.folderid;
        const section = props.match?.params?.section;

        if (folderId) {
            return folderId;
        }

        return section && /^\d+$/.test(section) ? section : '';
    }

    loadFolderState = (folderId) => {
        const routeState = this.props.location.state || {};
        const pageState = routeState.pages !== undefined ? routeState : null;

        if (!pageState || !Array.isArray(pageState.pages)) {
            return false;
        }

        this.setState({
            pages: pageState.pages,
            selectedFolderTitle: pageState.selectedFolderTitle || this.getFolderTitle(folderId),
            pageError: pageState.pageError || '',
            searchValue: pageState.searchValue || '',
            searchLoading: false,
            pageLoading: false
        });

        return true;
    }

    setApiError = (error, fallbackMessage) => {
        const status = error?.response?.status;

        if (status === 401 || status === 404) {
            this.setState({
                errorpageredirect: true,
                errorType: status === 401 ? 'expiryError' : 'noData',
                pageLoading: false,
                searchLoading: false
            });
            return;
        }

        this.setState({
            pageError: fallbackMessage,
            pageLoading: false,
            searchLoading: false
        });
    }

    loadFolders = () => {
        this.setState({ pageLoading: true, pageError: '', pages: [], selectedFolderTitle: '' });

        axios.get(getSellerUrl(jiraConfluenceFolderList), getSellerHeaders())
            .then(response => {
                this.setState({
                    folders: response?.data?.data?.filter(item => item?.id) || [],
                    pageLoading: false
                });
            })
            .catch(error => this.setApiError(error, 'Help folders are not available.'));
    }

    loadPages = () => {
        const folderId = this.getFolderId();

        this.setState({
            pageLoading: true,
            searchValue: '',
            pageError: '',
            pages: [],
            selectedFolderTitle: ''
        });

        axios.get(getSellerUrl(`${jiraConfluencePageList}${folderId}`), getSellerHeaders())
            .then(response => {
                this.setState({
                    pages: response.data?.data || [],
                    selectedFolderTitle: response.data?.data[0]?.foldername,
                    pageLoading: false
                });
            })
            .catch(error => {
                this.setState({ pages: [] });
                this.setApiError(error, 'No articles found for this folder.');
            });
    }

    loadSearchData = () => {
        const folderId = this.getFolderId();
        const searchValue = String(this.state.searchValue || '').trim();

        if (!searchValue) {
            this.loadPages();
            return;
        }

        const data = {
            data: {
                searchtext: searchValue
            }
        };

        this.setState({ searchLoading: true, pageError: '' });

        axios.post(getSellerUrl(`${jiraConfluenceSearchPageView}${folderId}`), JSON.stringify(data), getSellerHeaders())
            .then(response => {
                const pages = response.data?.data || [];

                this.setState({
                    pages,
                    selectedFolderTitle: this.getFolderTitle(folderId),
                    pageError: pages.length ? '' : `No results found for "${searchValue}".`,
                    searchLoading: false
                });
            })
            .catch(error => this.setApiError(error, 'Search failed. Please try again.'));
    }

    setSearchValue = (searchValue) => {
        this.setState({ searchValue });
    }

    handleSearchKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            this.loadSearchData();
        }
    }

    getFolderTitle = (folderId) => {
        const routeTitle = this.props.location?.state?.selectedFolderTitle;
        const folder = this.state.folders.find(item => String(item.id) === String(folderId));
        return routeTitle || folder?.title || 'Help Articles';
    }

    getExternalUrl = (item) => {
        const url = String(item?.url || item?.openUrl || '').trim();
        const markdownUrlMatch = url.match(/\((https?:\/\/[^)]+)\)/);

        return markdownUrlMatch?.[1] || url;
    }

    openFolder = (folder) => {
        if (folder?.type === 'external-link') {
            const externalUrl = this.getExternalUrl(folder);

            if (externalUrl) {
                window.open(externalUrl, '_blank', 'noopener,noreferrer');
            }

            return;
        }

        if (!folder?.id) {
            return;
        }

        this.props.history.push(`${FAQ_BASE_PATH}/folder/${folder.id}`, { selectedFolderTitle: folder.title });
    }

    openPage = (page) => {
        const pageId = page?.id || page?.contentId || page?.content_id || page?.pageId;

        if (!pageId) {
            return;
        }

        const { pages, pageError, searchValue, selectedFolderTitle } = this.state;
        const returnState = {
            pages,
            selectedFolderTitle,
            pageError,
            searchValue
        };

        this.props.history.push(`${FAQ_BASE_PATH}/article/${pageId}`, {
            returnTo: this.props.location.pathname,
            returnState
        });
    }

    render() {
        if (this.state.errorpageredirect) {
            return <NoData {...this.props} type={this.state.errorType} />;
        }

        return (
            <div className="faq-help-page">
                {this.state.pageLoading ? (
                    <div className="loader d-flex align-items-center justify-content-center" style={{ height: '100%' }}>
                        <img src={`${process.env.PUBLIC_URL}/static/icons/table/option_1.gif`} alt="Please wait ....." height="100px" width="100px" />
                    </div>
                ) : (
                    <FAQFolderList
                        history={this.props.history}
                        folderId={this.getFolderId()}
                        folders={this.state.folders}
                        pages={this.state.pages}
                        selectedFolderTitle={this.state.selectedFolderTitle}
                        pageError={this.state.pageError}
                        searchValue={this.state.searchValue}
                        searchLoading={this.state.searchLoading}
                        onSearchChange={this.setSearchValue}
                        onSearchKeyDown={this.handleSearchKeyDown}
                        onSearch={this.loadSearchData}
                        onFolderClick={this.openFolder}
                        onPageClick={this.openPage}
                    />
                )}
            </div>
        );
    }
}

export default connect(state => state)(FAQ);
